import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AccountTicketsNgrxFacade,
    ApplicationFacade,
    PurchaseFacade,
    PurchaseHistoryFacade,
    SessionService,
    ShoppingCartService,
    Ticket
} from '@ridango/components';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { LoadingController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { BaseComponent, NetworkService } from '@ridango/core';
import Env from '@ridango/core/env';
import { CartTicketRequest } from '@ridango/components/lib/purchase/purchase.model';
import { CardType } from '@ridango/components/lib/purchase/purchase.model';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { TicketsService } from '@ridango/core/lib/services';
import { CardsService } from '@ridango/components/lib/cards-ngrx/services/cards.service';

@Component({
    templateUrl: './ticket-details-page.component.html'
})
export class TicketDetailsPageComponent extends BaseComponent implements OnInit, OnDestroy {
    public ticket$ = this.route.paramMap.pipe(
        switchMap((params) => {
            const ticketId = params.get('id');
            const navigationState = window.history.state;
            if (navigationState?.ticket && navigationState?.isCardTicket) {
                return this.addCardNumberIfMissing(navigationState.ticket);
            }
            return this.ticketsService.tickets$.pipe(
                map((tickets) =>
                    tickets.find((ticket) => ticket.docNum?.toString() === ticketId || ticket.id === ticketId)
                ),
                switchMap((localTicket) => {
                    if (localTicket) {
                        return this.addCardNumberIfMissing(localTicket);
                    }

                    return this.accountTicketsFacade.fetchAccountTicketById(ticketId).pipe(
                        switchMap((apiTicket) => this.addCardNumberIfMissing(apiTicket)),
                        catchError((error) => {
                            console.error('Failed to load ticket:', error);
                            return of({} as Ticket);
                        })
                    );
                })
            );
        })
    );

    public isHistoryTicket = false;
    public isRecentlyUsedTicket = false;
    readonly BUY_TICKET_AGAIN = 'BUY_TICKET_AGAIN';
    readonly assetsPath$ = this.applicationFacade.assetsPath$;
    public cardType = CardType;

    physicalBackButtonSubscription$: Subscription;

    public isOnline$ = this.networkService.getStatus();
    private readonly DOWNLOAD_RECEIPT = 'Download receipt';
    private readonly TICKET_DETAILS = 'Ticket details';

    constructor(
        private route: ActivatedRoute,
        private historyFacade: PurchaseHistoryFacade,
        private accountTicketsFacade: AccountTicketsNgrxFacade,
        private loadingController: LoadingController,
        private applicationFacade: ApplicationFacade,
        private translate: TranslateService,
        private router: Router,
        private shoppingCartService: ShoppingCartService,
        private sessionService: SessionService,
        @Inject('ENV') public env: Env,
        private purchaseFacade: PurchaseFacade,
        private networkService: NetworkService,
        private platform: Platform,
        private analyticsService: AnalyticsService,
        private ticketsService: TicketsService,
        private cardsService: CardsService
    ) {
        super();
    }

    ngOnInit(): void {
        this.platform.backButton.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            this.handleBackClicked();
        });
        if (this.env.TICKET_DETAILED_INFORMATION) {
            let categoriesRequested = false;

            this.purchaseFacade
                .getCategoryProductDetails()
                .pipe(
                    takeUntil(this.componentDestroyed$),
                    filter((result) => !result?.length)
                )
                .subscribe(() => {
                    if (!categoriesRequested) {
                        // it is heavy request for BE. request it just ones. if result isn't empty array then request was called. if result is null then error was caught
                        this.purchaseFacade.fetchCategoryProductDetails();
                        categoriesRequested = true;
                    }
                });
        }

        this.isHistoryTicket = this.route.snapshot.queryParams['isHistory']?.toLowerCase() === 'true';
        this.isRecentlyUsedTicket = this.route.snapshot.queryParams['recentlyUsed']?.toLowerCase() === 'true';

        this.accountTicketsFacade.combinedTickets$.pipe(takeUntil(this.componentDestroyed$), take(1)).subscribe((t) => {
            if (t?.length === 0) {
                this.updateTicketsCache();
            }
        });
    }

    updateTicketsCache(): void {
        this.ticketsService.reloadTickets();
    }

    buyTicketAgain(ticket: Ticket): void {
        const payload: CartTicketRequest = {
            endZoneId: ticket.endZoneId || null,
            startZoneId: ticket.startZoneId || null,
            productId: ticket.productId.toString(),
            email: this.sessionService.getLoggedInEmail()
        };
        this.shoppingCartService
            .addProductToCartV2({ shoppingCartId: null, tickets: [payload] })
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                this.router.navigate([this.translate.currentLang, 'purchase-v2', 'cart']);
            });
    }

    async downloadPdf(ticket?: Ticket) {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.TICKETS,
            name: `${this.TICKET_DETAILS} - ${this.DOWNLOAD_RECEIPT}`
        });

        const loader = await this.loadingController.create({
            message: this.translate.instant('BUTTON_LOADER_TEXT')
        });
        await loader.present();
        await this.historyFacade.getPdfPut(ticket?.pdfToken, ticket?.id?.toString());
        await loader.dismiss();
    }

    handleBackClicked(): void {
        const query = this.isHistoryTicket ? 'isHistory=true' : 'checkTicketsForCertainTime=true';
        const route = `${this.translate.currentLang}/account/tickets?${query}`;
        this.router.navigateByUrl(route);
    }

    private addCardNumberIfMissing<
        T extends {
            cardId?: string | number;
            cardNumber?: string;
        }
    >(ticket: T): Observable<T> {
        if (ticket?.cardNumber || !ticket?.cardId) {
            return of(ticket);
        }
        return this.cardsService.getCustomerCards().pipe(
            map((cards) => {
                const card = cards?.find((card) => card.rawCardNumber === ticket.cardId?.toString());
                return {
                    ...ticket,
                    cardNumber: card?.cardNumber
                };
            }),
            catchError((error) => {
                console.error('Failed to load customer cards:', error);
                return of(ticket);
            })
        );
    }
}
